/**
 * Loading placeholders.
 *
 * Each one mirrors the shape of the content that replaces it — same card
 * radius, same padding, same rhythm — so loading reads as arrival rather than
 * as a swap. The pulse is slow and symmetric; a waiting state should not feel
 * impatient.
 */

export function SkeletonBlock({ width = "100%", height = 14, radius = "var(--radius-sm)", style = {} }) {
  return (
    <div
      className="shell-skel"
      style={{
        width,
        height,
        borderRadius: radius,
        ...style,
      }}
    />
  );
}

/** Wraps a skeleton so assistive tech hears "loading" once, not a pile of divs. */
function Loading({ children, style }) {
  return (
    <div aria-busy="true" style={style}>
      <span className="sr-only">Loading</span>
      <div aria-hidden="true">{children}</div>
    </div>
  );
}

export function SkeletonCoachHero() {
  return (
    <Loading style={{ marginBottom: "var(--stack-lg)" }}>
      <div className="shell-skel-hero">
        <SkeletonBlock width={92} height={12} radius="var(--radius-pill)" />
        <SkeletonBlock width="88%" height={20} style={{ marginTop: "var(--stack)" }} />
        <SkeletonBlock width="64%" height={20} style={{ marginTop: "var(--space-2)" }} />
        <SkeletonBlock width="46%" height={13} style={{ marginTop: "var(--stack-sm)" }} />
        <SkeletonBlock
          width="100%"
          height={52}
          radius="var(--radius)"
          style={{ marginTop: "var(--stack-lg)" }}
        />
      </div>
    </Loading>
  );
}

export function SkeletonWorkout() {
  return (
    <Loading style={{ marginBottom: "var(--section)" }}>
      <SkeletonBlock width={110} height={11} style={{ marginBottom: "var(--stack-sm)" }} />
      <div className="shell-skel-card">
        <SkeletonBlock width="70%" height={17} />
        <SkeletonBlock width="42%" height={13} style={{ marginTop: "var(--stack-sm)" }} />
        <SkeletonBlock
          width="100%"
          height={44}
          radius="var(--radius)"
          style={{ marginTop: "var(--stack)" }}
        />
      </div>
    </Loading>
  );
}

export function SkeletonPlan() {
  return (
    <Loading style={{ marginBottom: "var(--section)" }}>
      <SkeletonBlock width={100} height={11} style={{ marginBottom: "var(--stack-sm)" }} />
      <div className="shell-skel-plan">
        {[0, 1, 2].map((i) => (
          <div key={i} className="shell-skel-row">
            <SkeletonBlock width={28} height={28} radius="50%" style={{ flexShrink: 0 }} />
            <div className="shell-skel-row-lines">
              <SkeletonBlock width="34%" height={10} />
              <SkeletonBlock width="68%" height={14} style={{ marginTop: "var(--space-2)" }} />
            </div>
          </div>
        ))}
      </div>
    </Loading>
  );
}

export function SkeletonWeekDots() {
  return (
    <Loading style={{ marginBottom: "var(--section)" }}>
      <div className="shell-skel-weekhead">
        <SkeletonBlock width={88} height={11} />
        <SkeletonBlock width={28} height={11} />
      </div>
      <div className="shell-skel-week">
        {[0, 1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i}>
            <SkeletonBlock width={14} height={8} />
            <SkeletonBlock width={12} height={12} radius="50%" />
          </div>
        ))}
      </div>
    </Loading>
  );
}
